import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
    AlertTriangle, X, Check, Eye, Clock, 
    Car, User, Calendar, ChevronRight, Bell
} from 'lucide-react';
import { toast } from 'sonner';

export default function DamageAlertModal({ isOpen, onClose, alerts = [], onAcknowledge }) {
    const { language } = useLanguage();
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [acknowledging, setAcknowledging] = useState(false);

    const severityColors = {
        low: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
        medium: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
        high: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' }
    };

    const severityLabels = {
        low: language === 'el' ? 'Χαμηλή' : 'Low',
        medium: language === 'el' ? 'Μέτρια' : 'Medium',
        high: language === 'el' ? 'Υψηλή' : 'High'
    };

    const handleAcknowledge = async (alertId) => {
        setAcknowledging(true);
        try {
            await api.put(`/photo-comparison/alerts/${alertId}/acknowledge`);
            toast.success(language === 'el' ? 'Η ειδοποίηση επιβεβαιώθηκε' : 'Alert acknowledged');
            if (onAcknowledge) {
                onAcknowledge(alertId);
            }
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία επιβεβαίωσης' : 'Failed to acknowledge');
        } finally {
            setAcknowledging(false);
        }
    };

    const handleAcknowledgeAll = async () => {
        setAcknowledging(true);
        try {
            for (const alert of alerts) {
                await api.put(`/photo-comparison/alerts/${alert.id}/acknowledge`);
            }
            toast.success(language === 'el' ? 'Όλες οι ειδοποιήσεις επιβεβαιώθηκαν' : 'All alerts acknowledged');
            if (onAcknowledge) {
                onAcknowledge('all');
            }
            onClose();
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία επιβεβαίωσης' : 'Failed to acknowledge');
        } finally {
            setAcknowledging(false);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (alerts.length === 0) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-lg bg-white p-0 overflow-hidden">
                <DialogHeader className="p-4 pb-2 border-b border-slate-200 bg-red-50">
                    <DialogTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="w-5 h-5" />
                        {language === 'el' ? 'Ανιχνεύθηκαν Ζημιές' : 'Damage Detected'}
                        <span className="ml-auto text-sm font-normal bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                            {alerts.length}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="h-[400px]">
                    <div className="p-4 space-y-3">
                        {alerts.map((alert) => {
                            const colors = severityColors[alert.severity] || severityColors.low;
                            
                            return (
                                <div
                                    key={alert.id}
                                    className={`border-2 rounded-lg p-3 ${colors.border} ${colors.bg} transition-all hover:shadow-md`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            {/* Severity Badge */}
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${colors.bg} ${colors.text} border ${colors.border}`}>
                                                    {alert.severity === 'high' && '🔴'}
                                                    {alert.severity === 'medium' && '🟠'}
                                                    {alert.severity === 'low' && '🟡'}
                                                    {' '}{severityLabels[alert.severity]}
                                                </span>
                                            </div>

                                            {/* Vehicle & Driver Info */}
                                            <div className="space-y-1 text-sm">
                                                <div className="flex items-center gap-2 text-slate-700">
                                                    <Car className="w-4 h-4 text-slate-400" />
                                                    <span className="font-medium">{alert.vehicle_info}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <User className="w-4 h-4 text-slate-400" />
                                                    <span>{alert.driver_name}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-slate-500 text-xs">
                                                    <Calendar className="w-3 h-3 text-slate-400" />
                                                    <span>{formatDate(alert.created_at)}</span>
                                                </div>
                                            </div>

                                            {/* Damage Description */}
                                            <p className="mt-2 text-sm text-slate-700">
                                                {language === 'el' ? alert.damage_description_el : alert.damage_description_en}
                                            </p>

                                            {/* Photo Type */}
                                            <p className="mt-1 text-xs text-slate-500">
                                                📷 {language === 'el' ? 'Θέση' : 'Position'}: {alert.photo_type}
                                            </p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex flex-col gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleAcknowledge(alert.id)}
                                                disabled={acknowledging}
                                                className="text-xs"
                                            >
                                                <Check className="w-3 h-3 mr-1" />
                                                OK
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => window.location.href = '/app/comparison'}
                                                className="text-xs text-blue-600"
                                            >
                                                <Eye className="w-3 h-3 mr-1" />
                                                {language === 'el' ? 'Δες' : 'View'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="text-slate-600"
                    >
                        {language === 'el' ? 'Κλείσιμο' : 'Close'}
                    </Button>
                    <Button
                        onClick={handleAcknowledgeAll}
                        disabled={acknowledging}
                        className="bg-green-600 hover:bg-green-700"
                    >
                        <Check className="w-4 h-4 mr-2" />
                        {language === 'el' ? 'Επιβεβαίωση Όλων' : 'Acknowledge All'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// Hook to fetch and manage damage alerts
export function useDamageAlerts() {
    const { user, isAuthenticated } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const fetchAlerts = useCallback(async () => {
        if (!isAuthenticated || !user || !['admin', 'fleet_manager'].includes(user.role)) {
            return;
        }

        try {
            setLoading(true);
            const response = await api.get('/photo-comparison/alerts/unacknowledged');
            const newAlerts = response.data || [];
            
            // Show toast for new high-severity alerts
            if (newAlerts.length > 0) {
                const highSeverity = newAlerts.filter(a => a.severity === 'high');
                if (highSeverity.length > 0) {
                    toast.error(`🚨 ${highSeverity.length} σοβαρές ζημιές ανιχνεύθηκαν!`, {
                        duration: 10000,
                        action: {
                            label: 'Δες',
                            onClick: () => setShowModal(true)
                        }
                    });
                } else if (newAlerts.length > 0) {
                    toast.warning(`⚠️ ${newAlerts.length} ζημιές ανιχνεύθηκαν`, {
                        duration: 5000,
                        action: {
                            label: 'Δες',
                            onClick: () => setShowModal(true)
                        }
                    });
                }
                
                setShowModal(true);
            }
            
            setAlerts(newAlerts);
        } catch (error) {
            console.error('Failed to fetch damage alerts:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, user]);

    const handleAcknowledge = useCallback((alertId) => {
        if (alertId === 'all') {
            setAlerts([]);
        } else {
            setAlerts(prev => prev.filter(a => a.id !== alertId));
        }
    }, []);

    // Fetch alerts on mount and every 30 seconds
    useEffect(() => {
        fetchAlerts();
        const interval = setInterval(fetchAlerts, 30000);
        return () => clearInterval(interval);
    }, [fetchAlerts]);

    return {
        alerts,
        loading,
        showModal,
        setShowModal,
        fetchAlerts,
        handleAcknowledge
    };
}
